'use client'

import { useState, useTransition } from 'react'
import { useRouter } from 'next/navigation'
import { Check, Languages } from 'lucide-react'
import { toast } from '@/lib/toast'

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { Button } from '@/components/elements/Button'
import { useLocale, useT } from './LocaleProvider'
import { changeLocale } from '@/lib/i18n/actions'
import { writeLocaleCookie } from '@/lib/i18n/cookie'
import { LOCALES, LOCALE_META, LOCALE_STORAGE_KEY, type Locale } from '@/lib/i18n/config'

/**
 * Language picker.
 *
 * Each language is listed under its own name — a visitor looking for Russian is
 * looking for "Русский", not for "Russian" spelled out in a language they do
 * not read. For the same reason the list is never translated.
 */
export function LanguageSwitcher({ align = 'end' }: { align?: 'start' | 'center' | 'end' }) {
  const { locale } = useLocale()
  const t = useT()
  const router = useRouter()
  const [pending, startTransition] = useTransition()
  const [open, setOpen] = useState(false)

  const select = (next: Locale) => {
    setOpen(false)
    if(next === locale || pending) return

    // Write the cookie here rather than waiting for the action to come back.
    // The server action sets it too — this one just means the change has
    // already landed by the time the re-render arrives, instead of the UI
    // sitting in the old language for a round trip.
    writeLocaleCookie(next)
    try { window.localStorage.setItem(LOCALE_STORAGE_KEY, next) } catch { /* storage disabled */ }

    startTransition(async () => {
      const result = await changeLocale(next)
      if('error' in result) {
        toast.error(t('auth.toast.localeFailed'))
        return
      }
      // The whole tree is server-rendered in the old language; nothing short of
      // re-fetching it will change that.
      router.refresh()
    })
  }

  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger asChild>
        <Button variant="outline" size="icon" tag="button" aria-label={t('locale.change')}>
          <Languages className="h-[1.2rem] w-[1.2rem]" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align={align} className="min-w-40">
        <DropdownMenuLabel>{t('locale.label')}</DropdownMenuLabel>
        <DropdownMenuSeparator />
        { LOCALES.map(item => (
          <DropdownMenuItem
            key={item}
            onSelect={() => select(item)}
            // `lang` so a screen reader announces each name in its own language
            // rather than reading Cyrillic or Han characters through the
            // interface language's voice.
            lang={LOCALE_META[item].htmlLang}
            className="flex items-center justify-between gap-4 cursor-pointer"
          >
            <span>{LOCALE_META[item].label}</span>
            { item === locale && <Check className="h-4 w-4 shrink-0" aria-hidden="true" /> }
          </DropdownMenuItem>
        )) }
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
